import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./AdminUserEdit.css";

const AdminUserEdit = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const userId = location.state?.userId;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [roles, setRoles] = useState([]);
    const [states, setStates] = useState([]);
    const [senatorialDistricts, setSenatorialDistricts] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [wards, setWards] = useState([]);
    const [openSection, setOpenSection] = useState(null);
    const [search, setSearch] = useState({ states: "", sd: "", lgas: "", wards: "" });

    const [form, setForm] = useState({
        name: "", email: "", roleId: "", accessScope: "all", accessIds: [], isActive: false,
    });

    useEffect(() => {
        (async () => {
            try {
                const [userRes, rolesRes, statesRes, sdsRes, lgasRes, wardsRes] = await Promise.all([
                    privateApi.get(`/api/users/${userId}`),
                    privateApi.get("/api/roles"),
                    privateApi.get("/api/user/states"),
                    privateApi.get("/api/user/senatorial-districts"),
                    privateApi.get("/api/user/lgas"),
                    privateApi.get("/api/user/wards"),
                ]);
                const user = userRes.data.data;
                setRoles(rolesRes.data.data || []);
                setStates(statesRes.data.data || []);
                setSenatorialDistricts(sdsRes.data.data || []);
                setLgas(lgasRes.data.data || []);
                setWards(wardsRes.data.data || []);
                setForm({
                    name: user.name || "",
                    email: user.email || "",
                    roleId: user.roleId?._id || "",
                    accessScope: user.accessScope || "all",
                    accessIds: (user.accessIds || []).map(id => id.toString()),
                    isActive: user.isActive || false,
                });
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        })();
    }, [userId]);

    const handleChange = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const handleCheckboxChange = (id, checked) => {
        const idStr = id.toString();
        setForm(p => ({
            ...p,
            accessIds: checked
                ? [...new Set([...p.accessIds, idStr])]
                : p.accessIds.filter(x => x !== idStr),
        }));
    };

    const handleSelectAll = (items, checked) => {
        const ids = items.map(i => i._id.toString());
        setForm(p => ({
            ...p,
            accessIds: checked
                ? [...new Set([...p.accessIds, ...ids])]
                : p.accessIds.filter(id => !ids.includes(id)),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                accessScope: form.accessScope,
                isActive: form.isActive,
            };
            if (form.roleId?.trim()) payload.roleId = form.roleId;
            if (form.accessScope !== "all") {
                payload.accessIds = form.accessIds.filter(id => id && id.trim() !== "");
            }
            await privateApi.patch(`/api/users/${userId}`, payload);
            alert("User updated successfully");
            navigate("/admin/users");
        } catch (err) {
            console.error(err);
            alert("Failed to update user");
        } finally { setSaving(false); }
    };

    const renderSection = (title, items, key) => {
        if (!Array.isArray(items)) return null;
        const filtered = items.filter(i => i.name.toLowerCase().includes((search[key] || "").toLowerCase()));
        const allIds = items.map(i => i._id.toString());
        const allSelected = allIds.length > 0 && allIds.every(id => form.accessIds.includes(id));
        const isOpen = openSection === key;

        return (
            <div className={`adm-filter-box ${isOpen ? "active" : ""}`} key={key}>
                <div className="adm-filter-header" onClick={() => setOpenSection(p => p === key ? null : key)}>
                    <span>{title}</span>
                    {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </div>
                {isOpen && (
                    <div className="adm-filter-body">
                        <input className="adm-filter-search" placeholder={`Search ${title.toLowerCase()}…`}
                            value={search[key]} onChange={e => setSearch(p => ({ ...p, [key]: e.target.value }))} />
                        <label className="adm-select-all-label">
                            <input type="checkbox" checked={allSelected}
                                onChange={e => handleSelectAll(items, e.target.checked)} />
                            Select All
                        </label>
                        <div className="adm-filter-list">
                            {filtered.map(item => {
                                const id = item._id.toString();
                                return (
                                    <label key={id} className="adm-checkbox-item">
                                        <input type="checkbox" checked={form.accessIds.includes(id)}
                                            onChange={e => handleCheckboxChange(id, e.target.checked)} />
                                        {item.name}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <Spinner loadingInfo="Loading user details…" />;

    return (
        <div className="adm-page">
            <div className="adm-inner">
                <h1 className="adm-page-title">User Management</h1>

                <div className="adm-form-card">
                    <div className="adm-form-header">
                        <h2>Edit User</h2>
                        <p>Update account credentials and access permissions</p>
                    </div>

                    <div className="adm-form-body">
                        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                            <div className="adm-form-section-title">Account Details</div>

                            <div className="adm-form-group">
                                <label className="adm-label required">Full Name</label>
                                <input className="adm-input" type="text" value={form.name} required
                                    onChange={e => handleChange("name", e.target.value)} />
                            </div>

                            <div className="adm-form-group">
                                <label className="adm-label required">Email Address</label>
                                <input className="adm-input" type="email" value={form.email} required
                                    onChange={e => handleChange("email", e.target.value)} />
                            </div>

                            <div className="adm-form-divider" />
                            <div className="adm-form-section-title">Role &amp; Access</div>

                            <div className="adm-form-group">
                                <label className="adm-label required">Role</label>
                                <select className="adm-select" value={form.roleId} required
                                    onChange={e => handleChange("roleId", e.target.value)}>
                                    <option value="">Select role…</option>
                                    {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                </select>
                            </div>

                            <div className="adm-form-group">
                                <label className="adm-label">Access Scope</label>
                                <select className="adm-select" value={form.accessScope}
                                    onChange={e => handleChange("accessScope", e.target.value)}>
                                    <option value="all">All</option>
                                    <option value="state">State</option>
                                    <option value="senatorial_district">Senatorial District</option>
                                    <option value="lga">LGA</option>
                                    <option value="ward">Ward</option>
                                </select>
                            </div>

                            <div className="adm-access-sections">
                                {renderSection("States", states, "states")}
                                {renderSection("Senatorial Districts", senatorialDistricts, "sd")}
                                {renderSection("LGAs", lgas, "lgas")}
                                {renderSection("Wards", wards, "wards")}
                            </div>

                            <div className="adm-form-divider" />
                            <div className="adm-form-section-title">Account Status</div>

                            <label className="adm-toggle-row">
                                <input type="checkbox" checked={form.isActive}
                                    onChange={e => handleChange("isActive", e.target.checked)} />
                                <span>Active — user can log in and access the system</span>
                            </label>

                            <div className="adm-form-actions">
                                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => navigate(-1)}>
                                    ← Cancel
                                </button>
                                <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
                                    {saving ? "Saving…" : "✓ Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminUserEdit;